import { IInvoiceServiceCharge } from 'containers/invoices/invoices.model';
import { FC, useEffect, useState } from 'react';
import { initServiceCharge } from '../constants';
import AddServiceChargeSection from './AddServiceChargeSection';
import { useAppDispatch, useAppSelector } from 'hooks';
import AddFromServiceChargeListModal from './AddFromServiceChargeListModal';
import { IServiceCharge } from 'containers/product-catalog/service-charges/serviceCharge.model';
import AddServiceChargeModal, {
  IAddServiceChargeModalConfig,
  addServiceChargeModalConfigInitState,
  serviceChargeModalDataInit
} from './AddServiceChargeModal';
import {
  EInvoiceCatalogElementType,
  EServiceChargeModalType,
  IInvoiceSaveServiceCharge,
  InvoiceDraftModalData
} from '../types';
import { useLazyGetServiceChargesQuery } from 'containers/product-catalog/service-charges/api';
import {
  selectCurrentTeam,
  selectCurrentTeamFeePermissions,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { RootState } from 'store/reducers';
import { VoidFn } from 'types/baseTypes';
import { GlobalServiceChargeTax } from 'containers/product-catalog/catalog.model';

interface AddServiceChargeWrapperProps {
  className?: string;
  isAddServiceChargeDisabled?: boolean;
  selectedServiceCharge?: IInvoiceServiceCharge;
  isEditInvoiceFlow?: boolean;
  selectIsBackFromCreateTax: (state: RootState) => boolean;
  selectInvoiceDraftData: (state: RootState) => any;
  onRemoveSelectedServiceCharge: VoidFn;
  onBackFromCreateTax: VoidFn;
  updateSelectedServiceCharge: (arg: IInvoiceServiceCharge) => void;
  selectGlobalLineItemTaxes: (state: RootState) => GlobalServiceChargeTax[];
  updateInvoiceModalData: (arg: InvoiceDraftModalData) => void;
  isFromBillingTab?: boolean;
}

const AddServiceChargeWrapper: FC<AddServiceChargeWrapperProps> = ({
  className = '',
  isAddServiceChargeDisabled = false,
  selectedServiceCharge = initServiceCharge,
  isEditInvoiceFlow = false,
  selectIsBackFromCreateTax,
  selectInvoiceDraftData,
  onRemoveSelectedServiceCharge,
  onBackFromCreateTax,
  updateSelectedServiceCharge,
  selectGlobalLineItemTaxes,
  updateInvoiceModalData,
  isFromBillingTab = false
}) => {
  const dispatch = useAppDispatch();
  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTax);
  const selectedInvoiceDraftData = useAppSelector(selectInvoiceDraftData);
  const catalogSettingsPermissions = useAppSelector(selectCurrentTeamFeePermissions);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { additionalConfig } = useAppSelector(selectUserDetails);

  const [showAddFromServiceChargeListModal, setShowAddFromServiceChargeListModal] = useState<boolean>(false);

  const [addServiceChargeModalConfig, setAddServiceChargeModalConfig] = useState<IAddServiceChargeModalConfig>(
    addServiceChargeModalConfigInitState
  );

  const [getServiceChargeQuery, serviceChargeState] = useLazyGetServiceChargesQuery();

  const showDraftModal = () => {
    const { modalConfig } = selectedInvoiceDraftData ?? {};
    const serviceChargeModalConfig: IAddServiceChargeModalConfig = {
      title: modalConfig?.title,
      open: true,
      shouldInitializeFields: true,
      enableDeleteFromInvoice: modalConfig?.enableDeleteFromInvoice ?? false,
      enableSaveToCatalog: modalConfig?.enableSaveToCatalog ?? false,
      saveToCatalog: modalConfig.saveToCatalog ?? false,
      data: modalConfig?.data,
      editMode: modalConfig?.editMode ?? false,
      draftFlow: modalConfig?.draftFlow,
      modalType: modalConfig?.modalType
    };
    setAddServiceChargeModalConfig(serviceChargeModalConfig);
    if (isBackFromCreateTaxRoute) {
      onBackFromCreateTax();
    }
  };

  useEffect(() => {
    if (isBackFromCreateTaxRoute) {
      if (
        selectedInvoiceDraftData &&
        selectedInvoiceDraftData.invoiceCatalogElementType === EInvoiceCatalogElementType.SERVICE_CHARGE
      ) {
        showDraftModal();
      }
    }
  }, []);

  useEffect(() => {
    if (!serviceChargeState.isFetching) {
      if (serviceChargeState.isSuccess) {
        if (serviceChargeState?.data?.data?.length > 0) {
          setShowAddFromServiceChargeListModal(true);
        } else {
          handleCreateOneTimeServiceCharge();
        }
      } else if (serviceChargeState.isError) {
        dispatch(
          showNotifier({
            duration: 1000,
            type: NotifierTypes.ERROR,
            message: {
              primaryMessage: 'Something went wrong, please try again after some time',
              isMessageHtml: false
            }
          })
        );
      }
    }
  }, [serviceChargeState.isFetching, serviceChargeState.isSuccess, serviceChargeState.isError]);

  const onAddServiceCharge = (): void => {
    getServiceChargeQuery({ limit: 1, page: 0, search: null, teamId: currentTeam?.id });
  };

  const onEditServiceCharge = (): void => {
    const config: IAddServiceChargeModalConfig = {
      title: 'Edit service charge',
      editMode: true,
      open: true,
      shouldInitializeFields: true,
      enableDeleteFromInvoice: true,
      enableSaveToCatalog: false,
      data: {
        amount: selectedServiceCharge.amount,
        id: selectedServiceCharge.id,
        name: selectedServiceCharge.name,
        percentage: selectedServiceCharge.percentage,
        taxes: selectedServiceCharge.taxes
      },
      modalType: EServiceChargeModalType.EDIT_SERVICE_CHARGE_MODAL
    };

    setAddServiceChargeModalConfig(config);
  };

  const onRemoveServiceCharge = (): void => {
    onRemoveSelectedServiceCharge();
  };

  const onCloseAddFromServiceChargeListModal = (): void => {
    setShowAddFromServiceChargeListModal(false);
  };

  const onServiceChargeItemClick = (serviceCharge: IServiceCharge) => {
    setShowAddFromServiceChargeListModal(false);
    const config: IAddServiceChargeModalConfig = {
      title: 'Add service charge',
      editMode: false,
      open: true,
      shouldInitializeFields: true,
      enableDeleteFromInvoice: false,
      enableSaveToCatalog: false,
      data: {
        amount: serviceCharge.amount,
        id: serviceCharge.id,
        name: serviceCharge.name,
        percentage: serviceCharge.percentage,
        taxes: serviceCharge.taxes
      },
      modalType: EServiceChargeModalType.CATALOG_SERVICE_CHARGE_MODAL
    };
    setAddServiceChargeModalConfig(config);
  };

  const onCloseAddServiceChargeModal = (): void => {
    setAddServiceChargeModalConfig(addServiceChargeModalConfigInitState);
  };

  const onSaveServiceChargeClick = (serviceChargeData: IInvoiceSaveServiceCharge): void => {
    updateSelectedServiceCharge(serviceChargeData.invoiceServiceCharge);
    onCloseAddServiceChargeModal();
  };

  const onModifyServiceChargeClick = (serviceCharge: IInvoiceServiceCharge): void => {
    updateSelectedServiceCharge(serviceCharge);
    onCloseAddServiceChargeModal();
  };

  const handleCreateOneTimeServiceCharge = (): void => {
    setShowAddFromServiceChargeListModal(false);
    const config: IAddServiceChargeModalConfig = {
      title:
        additionalConfig?.saveLineItemToCatalogue && catalogSettingsPermissions.manageServiceCharges
          ? 'Create a reusable service charge'
          : 'Create one-time service charge',
      editMode: false,
      open: true,
      shouldInitializeFields: false,
      enableDeleteFromInvoice: false,
      enableSaveToCatalog: true,
      modalType: EServiceChargeModalType.ONE_TIME_ADD_SERVICE_CHARGE_MODAL,
      data: serviceChargeModalDataInit
    };
    setAddServiceChargeModalConfig(config);
  };

  const onRemoveServiceChargeFromInvoice = (): void => {
    onCloseAddServiceChargeModal();
    onRemoveServiceCharge();
  };

  return (
    <div className={`${className}`}>
      <AddServiceChargeSection
        onAddServiceCharge={onAddServiceCharge}
        onEditServiceCharge={onEditServiceCharge}
        onRemoveServiceCharge={onRemoveServiceCharge}
        selectedServiceCharge={selectedServiceCharge}
        isAddServiceChargeDisabled={isAddServiceChargeDisabled}
        isFromBillingTab={isFromBillingTab}
        className={isFromBillingTab ? 'border-b-0 p-3' : ''}
      />
      {showAddFromServiceChargeListModal && (
        <AddFromServiceChargeListModal
          onServiceChargeClick={onServiceChargeItemClick}
          onClose={onCloseAddFromServiceChargeListModal}
          open={showAddFromServiceChargeListModal}
          onCreateOneTimeServiceCharge={handleCreateOneTimeServiceCharge}
        />
      )}
      {addServiceChargeModalConfig.open && (
        <AddServiceChargeModal
          isEditInvoiceFlow={isEditInvoiceFlow}
          onSaveServiceChargeClick={onSaveServiceChargeClick}
          onModifyServiceChargeClick={onModifyServiceChargeClick}
          onClose={onCloseAddServiceChargeModal}
          config={addServiceChargeModalConfig}
          onDeleteFromInvoice={onRemoveServiceChargeFromInvoice}
          selectGlobalLineItemTaxes={selectGlobalLineItemTaxes}
          updateInvoiceModalData={updateInvoiceModalData}
        />
      )}
    </div>
  );
};

export default AddServiceChargeWrapper;
